import axios from 'axios';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Image URL resolver ────────────────────────────────────────────────────
// Backend returns relative URLs like "/uploads/filename.jpg"
// We need: http://localhost:8080/api/uploads/filename.jpg
// So we strip /api from BASE_URL and prepend that as the host base.
const HOST_BASE = BASE_URL.replace(/\/api\/?$/, ''); // e.g. http://localhost:8080

export function getImageUrl(url) {
  if (!url) return null;
  // Already a full URL
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Do not rewrite /uploads paths to our HOST_BASE: prefer the raw path or
  // let the caller decide. Returning the input prevents the client from
  // making extra requests to `/uploads` on our host.
  if (url.startsWith('/uploads/') || url.startsWith('/api/uploads/')) return url;
  return url;
}

const api = axios.create({
  baseURL: BASE_URL,
});

// Attach JWT token to every request and ensure we don't force
// `application/json` for FormData requests (axios/browser will set
// the correct multipart boundary automatically).
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // If the request body is FormData, remove any Content-Type header so
  // the browser can set the correct multipart/form-data boundary.
  if (config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers && config.headers['Content-Type']) {
      delete config.headers['Content-Type'];
    }
  } else {
    // For non-FormData requests, default to JSON when content-type is not set.
    if (!config.headers['Content-Type']) config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password, role: 'ADMIN' }),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/admin/profile'),

  // Admin forgot-password (token-based, no OTP)
  // Step 1: submit email → receive a short-lived resetToken
  adminForgotPasswordRequest: (email) =>
    api.post('/auth/forgot-password/admin/request', { email }),

  // Step 2: submit resetToken + newPassword + confirmPassword → password updated
  adminForgotPasswordReset: (resetToken, newPassword, confirmPassword) =>
    api.post('/auth/forgot-password/admin/reset', { resetToken, newPassword, confirmPassword }),
};

// ── Dashboard ─────────────────────────────────────────────────────────────
export const dashboardAPI = {
  get: (period = 'Last 7 days') =>
    api.get('/admin/dashboard', { params: { period } }),
  getAnalytics: (period = 'Last 7 days') =>
    api.get('/admin/analytics/revenue', { params: { period } }),
  getTopSellers: (limit = 10) =>
    api.get('/admin/analytics/top-sellers', { params: { limit } }),
};

// ── Sellers ───────────────────────────────────────────────────────────────
export const sellersAPI = {
  getAll: (page = 0, size = 10) =>
    api.get('/admin/sellers', { params: { page, size } }),
  getPending: (page = 0, size = 10) =>
    api.get('/admin/sellers/pending', { params: { page, size } }),
  getById: (id) => api.get(`/admin/sellers/${id}`),
  approve: (id) => api.post(`/admin/sellers/${id}/approve`),
  reject: (id, reason) => api.post(`/admin/sellers/${id}/reject`, { reason }),
  suspend: (id, reason) => api.post(`/admin/sellers/${id}/suspend`, { reason }),
};


// ── Orders ────────────────────────────────────────────────────────────────
export const ordersAPI = {
  getAll: (status, page = 0, size = 10) =>
    api.get('/admin/orders', { params: { status, page, size } }),
  getById: (id) => api.get(`/admin/orders/${id}`),
  assignDelivery: (orderId, deliveryPartnerId) =>
    api.put(`/admin/orders/${orderId}/assign-delivery`, { deliveryPartnerId }),
  // The backend sends the customer push (Preparing / Ready / Out for delivery /
  // Delivered) itself whenever the status changes — nothing extra to send here.
  updateStatus: (orderId, status) =>
    api.put(`/admin/orders/${orderId}/status`, { status }),
};

// ── Real-time order push (replaces 3s polling) ──────────────────────────────
// Connects once, and calls `onNewOrder` the instant a new order is placed.
// Call the returned `disconnect()` on unmount / logout.
let stompClient = null;

export function subscribeToNewOrders(accessToken, onNewOrder, onError) {
  stompClient = new Client({
    webSocketFactory: () => new SockJS(`${api.defaults.baseURL.replace(/\/$/, '')}/ws`),
    connectHeaders: { Authorization: `Bearer ${accessToken}` },
    reconnectDelay: 5000, // auto-reconnect if the connection drops
    onConnect: () => {
      stompClient.subscribe('/topic/admin/orders', (message) => {
        try {
          const event = JSON.parse(message.body);
          onNewOrder(event);
        } catch (e) {
          console.error('Failed to parse order event', e);
        }
      });
    },
    onStompError: (frame) => {
      console.error('WebSocket STOMP error', frame);
      onError?.(frame);
    },
  });

  stompClient.activate();

  return {
    disconnect: () => {
      stompClient?.deactivate();
      stompClient = null;
    },
  };
}

// ── Customers ─────────────────────────────────────────────────────────────
export const customersAPI = {
  getAll: (page = 0, size = 10) =>
    api.get('/admin/customers', { params: { page, size } }),
  getById: (id) => api.get(`/admin/customers/${id}`),
};

// ── Products ──────────────────────────────────────────────────────────────
export const productsAPI = {
  getPending: (page = 0, size = 20) =>
    api.get('/admin/products/pending', { params: { page, size } }),
  approve: (id, remark, adminCharges) =>
    api.post(`/admin/products/${id}/approve`, { remark, adminCharges: adminCharges ? Number(adminCharges) : 0 }),
  reject: (id, reason) =>
    api.post(`/admin/products/${id}/reject`, { reason }),
};

// ── Categories ────────────────────────────────────────────────────────────
export const categoriesAPI = {
  getAll: (page = 0, size = 10, status = null) =>
    api.get('/admin/categories', {
      params: { paged: true, page, size, ...(status ? { status } : {}) },
    }),

  getById: (id) => api.get(`/admin/categories/${id}`),

  // ✅ Upload image separately via /uploads (folder=categories)
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'categories');        // ← tell backend which S3 folder
    return api.post('/uploads', formData);          // ← no manual Content-Type
  },

  // ✅ Create — send as multipart/form-data (controller expects @RequestParam)
  create: (name, image, status = 'ACTIVE') => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('status', status);
    if (image) formData.append('image', image);     // image = File object
    return api.post('/admin/categories', formData); // ← no manual Content-Type
  },

  // ✅ Update — same, multipart
  update: (id, { name, image, status }) => {
    const formData = new FormData();
    if (name)   formData.append('name', name);
    if (status) formData.append('status', status);
    if (image)  formData.append('image', image);    // only if replacing image
    return api.put(`/admin/categories/${id}`, formData);
  },

  delete: (id) => api.delete(`/admin/categories/${id}`),
};
// ── Platform Settings ────────────────────────────────────────────────────
export const settingsAPI = {
  getDeliveryFee: () => api.get('/admin/settings/delivery-fee'),
  updateDeliveryFee: (deliveryFee, freeDeliveryAbove) =>
    api.put('/admin/settings/delivery-fee', { deliveryFee, freeDeliveryAbove }),
};

// ── Delivery Partners ─────────────────────────────────────────────────────
export const deliveryAPI = {
  getPending: (page = 0, size = 10) =>
    api.get('/admin/delivery-partners/pending', { params: { page, size } }),
  approve: (id) => api.post(`/admin/delivery-partners/${id}/approve`),
};

// ── Terms & Conditions (Seller) ──────────────────────────────────────────
export const termsAPI = {
  getAll: (forRole = 'SELLER') =>
    api.get('/admin/terms', { params: { forRole } }),
  create: (version, title, content, forRole = 'SELLER') =>
    api.post('/admin/terms', { version, title, content, forRole }),
  activate: (id) => api.put(`/admin/terms/${id}/activate`),
};

// ── Support Contact (admin edits; visible to sellers & customers) ─────────
export const supportAPI = {
  // Admin: read / update contact details
  get: () => api.get('/admin/support-contact'),
  update: (email, phone) => api.put('/admin/support-contact', { email, phone }),
};

// ── Coupons ───────────────────────────────────────────────────────────────
export const couponsAPI = {
  getAll: () => api.get('/admin/coupons'),

  create: (payload) => api.post('/admin/coupons', payload),

  update: (id, payload) => api.put(`/admin/coupons/${id}`, payload),

  setActive: (id, isActive) =>
    api.patch(`/admin/coupons/${id}/status`, { isActive }),

  delete: (id) => api.delete(`/admin/coupons/${id}`),
};

// ── Banners ───────────────────────────────────────────────────────────────
// Matches every call made in pages/Banners.jsx:
//   bannersAPI.getAll()
//   bannersAPI.create(fields, imageFile)
//   bannersAPI.update(id, fields, imageFile, removeImage)
//   bannersAPI.setStatus(id, status)   // 'ACTIVE' | 'INACTIVE'
//   bannersAPI.reorder(orderedIds)     // banner ids in display order
//   bannersAPI.delete(id)
//
// The page reads the list from `res.data.data`.
// Banner fields: id, label, title, subtitle, buttonText, actionType
// (NONE | CATEGORY | PRODUCT | SEARCH), actionValue, backgroundColor,
// backgroundColorEnd, textColor, imageShape (CIRCLE | ROUNDED | LEAF | ARCH |
// BLOB | DIAMOND), imageGradientMode (NONE | RING | OVERLAY), imageGradientStart,
// imageGradientEnd, status, startsAt, endsAt, image, live.
const BANNER_FIELDS = [
  'label', 'title', 'subtitle', 'buttonText',
  'actionType', 'actionValue',
  'backgroundColor', 'backgroundColorEnd', 'textColor',
  'imageShape', 'imageGradientMode', 'imageGradientStart', 'imageGradientMid', 'imageGradientEnd',
  'backgroundColors', 'decoration',
  'status', 'startsAt', 'endsAt',
];

const buildBannerForm = (fields, imageFile, removeImage) => {
  const formData = new FormData();

  // Always send every text field (even when empty) so clearing a field
  // such as subtitle or endsAt while editing really clears it.
  BANNER_FIELDS.forEach((key) => formData.append(key, fields[key] ?? ''));

  if (imageFile) formData.append('image', imageFile);       // new / replacement photo
  if (removeImage) formData.append('removeImage', 'true');  // photo cleared in edit mode

  return formData; // ← no manual Content-Type
};

export const bannersAPI = {
  getAll: () => api.get('/admin/banners'),

  create: (fields, imageFile) =>
    api.post('/admin/banners', buildBannerForm(fields, imageFile, false)),

  update: (id, fields, imageFile, removeImage = false) =>
    api.put(`/admin/banners/${id}`, buildBannerForm(fields, imageFile, removeImage)),

  setStatus: (id, status) =>
    api.patch(`/admin/banners/${id}/status`, { status }),

  reorder: (ids) =>
    api.post('/admin/banners/reorder', { ids }),

  delete: (id) => api.delete(`/admin/banners/${id}`),
};

// ── Reviews ───────────────────────────────────────────────────────────────
export const reviewsAPI = {
  getAll: (page = 0, size = 20) =>
    api.get('/admin/reviews', { params: { page, size } }),
  getByProduct: (productId, page = 0, size = 20) =>
    api.get(`/admin/products/${productId}/reviews`, { params: { page, size } }),
  delete: (reviewId) =>
    api.delete(`/admin/reviews/${reviewId}`),
};

export default api;